import * as amplitude from '@amplitude/analytics-browser';

import { detectAccessMode, detectBrowser, detectPlatform } from '../device';
import type { AnalyticsUser, EventProps, SuperProperties } from '../types';

const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

let sdkInitialized = false;

/**
 * Amplitude SDK を匿名で初期化する（ユーザー識別前でも track できる状態にする）。
 *
 * autocapture は FREE プラン運用のため pageViews のみ有効化する。
 * 二重初期化はモジュールレベルのフラグで防ぐ。
 *
 * @returns 検知したスーパープロパティ。API キー未設定時は null。
 */
export function ensureAmplitudeInitialized(): SuperProperties | null {
  if (!AMPLITUDE_API_KEY) return null;

  const accessMode = detectAccessMode();
  const devicePlatform = detectPlatform();
  const deviceBrowser = detectBrowser();

  if (sdkInitialized) {
    return { accessMode, devicePlatform, deviceBrowser };
  }
  sdkInitialized = true;

  amplitude.init(AMPLITUDE_API_KEY, {
    autocapture: {
      pageViews: true,
      elementInteractions: false,
      formInteractions: false,
      sessions: false,
      attribution: false,
      fileDownloads: false,
      networkTracking: false,
      webVitals: false,
      frustrationInteractions: false,
    },
  });

  return { accessMode, devicePlatform, deviceBrowser };
}

/**
 * サインイン済みユーザーで Amplitude のユーザー識別・属性設定を行う。
 * SDK が未初期化なら先に初期化する。
 *
 * @returns 検知したスーパープロパティ。API キー未設定・ユーザー未確定時は null。
 */
export function identifyAmplitude(user: AnalyticsUser): SuperProperties | null {
  if (!AMPLITUDE_API_KEY || !user.id) return null;

  const superProps = ensureAmplitudeInitialized();
  if (!superProps) return null;

  const { accessMode, devicePlatform, deviceBrowser } = superProps;

  amplitude.setUserId(user.id);

  const now = new Date().toISOString();
  const organizationName = user.organization?.name;
  const departmentName = user.department?.departmentName;
  const userRole = user.role;

  const identify = new amplitude.Identify();

  // 固定属性 + 最新値（Mixpanel の people.set 相当）
  identify.set('name', `${user.familyName?.value} ${user.givenName?.value}`);
  if (user.email) identify.set('email', user.email);
  if (user.thumbnail) identify.set('avatar', user.thumbnail);
  identify.set('userId', user.id);
  identify.set('accessMode', accessMode);
  identify.set('devicePlatform', devicePlatform);
  identify.set('deviceBrowser', deviceBrowser);
  identify.set('lastAccessMode', accessMode);
  identify.set('lastDevicePlatform', devicePlatform);
  identify.set('lastDeviceBrowser', deviceBrowser);
  identify.set('lastSeenAt', now);
  if (organizationName) {
    identify.set('organization', organizationName);
    identify.set('lastOrganization', organizationName);
  }
  if (departmentName) {
    identify.set('department', departmentName);
    identify.set('lastDepartment', departmentName);
  }
  if (userRole) {
    identify.set('role', userRole);
    identify.set('lastRole', userRole);
  }

  // 初回値のみ記録（Mixpanel の people.set_once 相当）
  identify.setOnce('firstAccessMode', accessMode);
  identify.setOnce('firstDevicePlatform', devicePlatform);
  identify.setOnce('firstDeviceBrowser', deviceBrowser);
  identify.setOnce('firstSeenAt', now);
  if (organizationName) identify.setOnce('firstOrganization', organizationName);
  if (departmentName) identify.setOnce('firstDepartment', departmentName);
  if (userRole) identify.setOnce('firstRole', userRole);

  // 履歴を配列にユニーク追加（Mixpanel の people.union 相当）
  identify.preInsert('accessModesUsed', accessMode);
  identify.preInsert('devicePlatformsUsed', devicePlatform);
  identify.preInsert('deviceBrowsersUsed', deviceBrowser);
  if (organizationName) identify.preInsert('organizationsUsed', organizationName);
  if (departmentName) identify.preInsert('departmentsUsed', departmentName);
  if (userRole) identify.preInsert('rolesUsed', userRole);

  amplitude.identify(identify);

  return superProps;
}

/**
 * Amplitude にイベントを送信する。
 * SDK 未初期化なら何もしない。
 * Amplitude にはグローバル register 相当が無いため、
 * スーパープロパティはファサードがマージして渡す。
 */
export function trackAmplitude(eventName: string, properties?: EventProps): void {
  if (!AMPLITUDE_API_KEY || !sdkInitialized) return;
  amplitude.track(eventName, properties);
}
